import { useState } from 'react';
import BasicModal from './BasicModal';
import { LinkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { ChromeExtensionTip } from '../ChromeExtensionTip';
import { isYoutubeShortUrl } from '../../routes/Homepage/Utils/GenerationUtils';

export const YoutubeTranscriptModal = ({
  showModal,
  setShowModal,
  youtubeUrl,
  onUseTranscript,
}) => {
  const [transcript, setTranscript] = useState('');

  // The extension reads YouTube on the watch page only, so a Short gets the
  // copy/paste path without the tip — pointing a teacher at an extension that
  // can't read the video they pasted is worse than not mentioning it. An empty
  // or unrecognized url isn't a Short, so it keeps the tip.
  const showExtensionTip = !isYoutubeShortUrl(youtubeUrl);

  const handleClose = () => {
    setTranscript('');
    setShowModal(false);
  };

  const handleUseTranscript = () => {
    onUseTranscript(transcript);
    setTranscript('');
    setShowModal(false);
  };

  return (
    <BasicModal
      icon={<LinkIcon className='h-6 w-6 text-emerald-600' aria-hidden='true' />}
      title='YouTube Links No Longer Supported'
      showModal={showModal}
      setShowModal={handleClose}
      maxWidth='sm:max-w-lg'>
      <div className='mt-4 space-y-4'>
        <p className='text-sm text-gray-700'>
          Due to YouTube&apos;s updated privacy and compliance guidelines, generating content
          directly from YouTube URLs is no longer supported.
        </p>
        <p className='text-sm text-gray-700'>
          You can still use YouTube content by copying the video&apos;s transcript and pasting it
          below.
        </p>

        {youtubeUrl && (
          <a
            href={youtubeUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 underline'>
            Open video on YouTube
            <ArrowRightIcon className='h-3.5 w-3.5' />
          </a>
        )}

        {showExtensionTip && (
          <ChromeExtensionTip source='youtube_transcript_modal'>
            works right on YouTube — no copying and pasting.
          </ChromeExtensionTip>
        )}

        <details className='text-sm text-gray-600'>
          <summary className='cursor-pointer hover:text-gray-800 font-medium'>
            How to get a YouTube transcript
          </summary>
          <ol className='mt-2 ml-4 list-decimal space-y-1'>
            <li>Open the video on YouTube</li>
            <li>Click &quot;...more&quot; under the video title</li>
            <li>Click the &quot;Show Transcript&quot; button</li>
            <li>Highlight the full text, right-click, and copy</li>
            <li>Paste it into the box below</li>
          </ol>
        </details>

        <textarea
          autoFocus
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder='Paste the YouTube transcript here...'
          className='w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-emerald-500 resize-y'
          rows={6}
        />

        <div className='flex gap-3'>
          <button
            onClick={handleUseTranscript}
            disabled={!transcript.trim()}
            className='flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'>
            Use transcript
          </button>
          <button
            onClick={handleClose}
            className='rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors'>
            Cancel
          </button>
        </div>
      </div>
    </BasicModal>
  );
};
